/**
 * Quiz Generation Context
 * Server-side background quiz generation with real progress.
 * The teacher can navigate anywhere (or refresh) while quizzes generate:
 * jobs live on the backend and this context polls them, so the tracker
 * reattaches to running jobs after a reload.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import {
  startQuizGeneration,
  getQuizGenerationJob,
  QuizGenerationJob,
  GenerateQuizParams,
} from '../api/courses';

export interface QuizGenTask {
  jobId: string;
  courseId: string;
  title: string;
  completed: number;
  total: number;
  status: 'queued' | 'running' | 'done' | 'failed';
  quizId?: string | null;
  error?: string | null;
}

interface QuizGenerationContextValue {
  quizTasks: QuizGenTask[];
  startQuizJob: (courseId: string, params: GenerateQuizParams) => Promise<void>;
  attachJobs: (jobs: QuizGenerationJob[]) => void;
  dismissQuizTask: (jobId: string) => void;
  dismissCompletedQuizTasks: () => void;
  hasActiveQuizGenerations: boolean;
  /** Bumped whenever a job finishes, so course pages can refresh their quiz list */
  completionVersion: number;
}

const QuizGenerationContext = createContext<QuizGenerationContextValue | undefined>(undefined);

const STORAGE_KEY = 'quiz_generation_jobs';

const toTask = (job: QuizGenerationJob): QuizGenTask => ({
  jobId: job.job_id,
  courseId: job.course_id,
  title: job.title,
  completed: job.completed_questions,
  total: job.total_questions,
  status: job.status,
  quizId: job.quiz_id,
  error: job.error,
});

export const QuizGenerationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [quizTasks, setQuizTasks] = useState<QuizGenTask[]>([]);
  const [completionVersion, setCompletionVersion] = useState(0);
  const autoDismissTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const hasActiveQuizGenerations = quizTasks.some(t => t.status === 'queued' || t.status === 'running');

  const scheduleAutoDismiss = useCallback((jobId: string) => {
    if (autoDismissTimers.current.has(jobId)) return;
    const timer = setTimeout(() => {
      autoDismissTimers.current.delete(jobId);
      setQuizTasks(prev => prev.filter(t => t.jobId !== jobId));
    }, 15000);
    autoDismissTimers.current.set(jobId, timer);
  }, []);

  // Persist active job ids so a page refresh can reattach
  useEffect(() => {
    const active = quizTasks
      .filter(t => t.status === 'queued' || t.status === 'running')
      .map(t => ({ jobId: t.jobId, courseId: t.courseId }));
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(active));
    } catch {
      // storage full or unavailable: polling still works for this session
    }
  }, [quizTasks]);

  // Reattach to jobs that were running before a refresh
  useEffect(() => {
    let stored: { jobId: string; courseId: string }[] = [];
    try {
      stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      stored = [];
    }
    if (!stored.length) return;
    (async () => {
      for (const { jobId, courseId } of stored) {
        try {
          const job = await getQuizGenerationJob(courseId, jobId);
          setQuizTasks(prev => (prev.some(t => t.jobId === job.job_id) ? prev : [...prev, toTask(job)]));
        } catch {
          // job gone or not ours anymore: drop it
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll active jobs every 4 seconds
  useEffect(() => {
    if (!hasActiveQuizGenerations) return;
    const interval = setInterval(async () => {
      const active = quizTasks.filter(t => t.status === 'queued' || t.status === 'running');
      for (const task of active) {
        try {
          const job = await getQuizGenerationJob(task.courseId, task.jobId);
          setQuizTasks(prev => prev.map(t => (t.jobId === job.job_id ? toTask(job) : t)));
          if (job.status === 'done' || job.status === 'failed') {
            setCompletionVersion(v => v + 1);
            if (job.status === 'done') scheduleAutoDismiss(job.job_id);
          }
        } catch {
          // transient network failure: keep polling
        }
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [hasActiveQuizGenerations, quizTasks, scheduleAutoDismiss]);

  // Warn before closing the tab: generation continues server-side, but set expectations
  useEffect(() => {
    const handleBeforeUnload = () => {
      // No blocking prompt needed: jobs run on the server and reattach on reload
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    return () => {
      autoDismissTimers.current.forEach(timer => clearTimeout(timer));
    };
  }, []);

  const startQuizJob = useCallback(async (courseId: string, params: GenerateQuizParams) => {
    const job = await startQuizGeneration(courseId, params);
    setQuizTasks(prev => [...prev, toTask(job)]);
  }, []);

  const attachJobs = useCallback((jobs: QuizGenerationJob[]) => {
    setQuizTasks(prev => {
      const known = new Set(prev.map(t => t.jobId));
      const fresh = jobs
        .filter(j => !known.has(j.job_id) && (j.status === 'queued' || j.status === 'running'))
        .map(toTask);
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  }, []);

  const dismissQuizTask = useCallback((jobId: string) => {
    const timer = autoDismissTimers.current.get(jobId);
    if (timer) {
      clearTimeout(timer);
      autoDismissTimers.current.delete(jobId);
    }
    setQuizTasks(prev => prev.filter(t => t.jobId !== jobId));
  }, []);

  const dismissCompletedQuizTasks = useCallback(() => {
    setQuizTasks(prev => prev.filter(t => t.status === 'queued' || t.status === 'running'));
  }, []);

  return (
    <QuizGenerationContext.Provider
      value={{
        quizTasks,
        startQuizJob,
        attachJobs,
        dismissQuizTask,
        dismissCompletedQuizTasks,
        hasActiveQuizGenerations,
        completionVersion,
      }}
    >
      {children}
    </QuizGenerationContext.Provider>
  );
};

export const useQuizGeneration = (): QuizGenerationContextValue => {
  const context = useContext(QuizGenerationContext);
  if (!context) {
    throw new Error('useQuizGeneration must be used within a QuizGenerationProvider');
  }
  return context;
};
